import * as React from 'react'
import {
  TABULAR_CONFIG_BUILD_ACCEPTED_EXTENSIONS,
  tabularFileExtension
} from '@atbash-labs/ontology-core'
import { PLANT_COMPUTE_DB_QUERY_PARAM, plantHref } from '@atbash-labs/plantview-links'
import {
  abortIngestUpload,
  downloadIntakeItemFile,
  downloadTabularRunInput,
  fetchIntakeItemFile,
  fetchTabularRunInput,
  useAcknowledgeTabularIngestJobMutation,
  useCommitTabularIngestJobMutation,
  useDiscardTabularIngestJobMutation,
  useGetAuthzMeQuery,
  useGetTabularConfigBuildQuery,
  useGetTabularIngestJobDetailQuery,
  useListIngestMappingConfigsQuery,
  useRebuildTabularIngestConfigMutation,
  useRejectTabularConfigDraftMutation,
  useRerunTabularIngestJobMutation,
  useStopIngestJobMutation,
  type IngestMappingConfigSummary,
  type TabularIngestJobDetail
} from '@axilon-apps/domains-ontology-ui-state'
import { ArrowLeft, Download, ExternalLink } from 'lucide-react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { JobLogs } from '../../components/JobLogs/JobLogs'
import { StatusPill } from '../../components/primitives/StatusPill'
import { ReplaceConfirmDialog } from '../../components/ReplaceConfirmDialog'
import { SourceDocViewer } from '../../components/SourceDocViewer/SourceDocViewer'
import { useSourceDocViewer } from '../../components/SourceDocViewer/useSourceDocViewer'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { replacementRequestOf, type ReplacementRequest } from '../../lib/replaced-files'
import { formatRelativeTime } from '../../lib/time'
import { cn } from '../../lib/utils'
import { CollapsibleCard } from '../IngestPage/shared/CollapsibleCard'
import { ConversationToolCallsCard } from '../IngestPage/shared/ConversationToolCallsCard'
import { NoteDialog } from '../IngestPage/shared/NoteDialog'
import { WriteButton } from '../IngestPage/shared/WriteButton'
import {
  COMPACT_ACTION,
  COMPACT_PAGE,
  DESTRUCTIVE_ACTION,
  DESTRUCTIVE_OUTLINE,
  META_TEXT,
  MONO_WRAP,
  SECTION_LABEL
} from '../IngestPage/shared/compact'
import { extractErrorMessage } from '../IngestPage/util/extractErrorMessage'
import { ConfigPicker, type SelectedTabularConfig } from '../IngestPage/wizard/ConfigPicker'
import { UpsertConfirmDialog } from '../IngestPage/wizard/UpsertConfirmDialog'
import { formatCallDurationMs, formatDurationMs } from '../../lib/format-duration'
import { formatBytes } from '../DocsHubPage/doc-helpers'
import { formatRunDuration } from '../RunPage/run-timing'
import { extensionList } from '../TabularConfigPage/util/configView'
import { ExtractedRowsTable, type ExtractedColumn, type PageLinks } from './ExtractedRowsTable'
import { PagesWorkspace } from './PagesWorkspace'
import {
  parseTabularRowRef,
  tabularConfigBuildPath,
  tabularConfigPath,
  tabularJobPagePath,
  tabularJobPath,
  tabularJobViewParams,
  type TabularJobView
} from './route'
import { tabularJobPill } from './tabularJobPill'
import { useRunPageReview, type RunPageReview } from './useRunPageReview'

const TERMINAL_STATES = new Set(['done', 'error', 'skipped', 'cancelled'])
/**
 * A job parked in awaiting_rebuild with no build in flight, held for review,
 * or waiting for earlier intake files changes only when someone acts on it
 * or on an earlier file. A held intake's turn comes from intakes, which
 * publish no wake-up, so a parked job keeps this deliberate slow poll; every
 * other state refreshes on `jobs` wake-ups.
 */
const PARKED_POLL_MS = 15_000
const HELD_STATES = new Set(['awaiting_review', 'awaiting_turn'])
const RERUNNABLE_STATES = new Set([
  'done',
  'error',
  'awaiting_rebuild',
  ...HELD_STATES,
  'skipped',
  'cancelled'
])
const STOPPABLE_STATES = new Set(['uploading', 'processing'])
/** Set by intake dispatch when the intake is pinned to a version that has since been archived. */
const CONFIG_ARCHIVED_ERROR = 'TABULAR_CONFIG_ARCHIVED'
/** Raised by the input-contract check, which runs before a run stores its files. */
const INPUT_REFUSED_ERRORS = new Set([
  'TABULAR_INPUT_EXTENSION',
  'TABULAR_INPUT_FILE_COUNT',
  'TABULAR_INPUT_FILES_CONTAIN_DUPLICATE_NAMES',
  'TABULAR_INPUT_FILE_NAME_INVALID',
  'TABULAR_INPUT_FILE_EMPTY'
])
const REVIEW_VALIDATION_FAILED = 'TABULAR_REVIEW_VALIDATION_FAILED'
const UNSTAGED_RERUN_TITLE =
  'This run failed before it stored its files, so it cannot run again. Start a new run with the files.'

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`
}

/**
 * What a run refused before its files reached the parser, and how to get past
 * it. `accept` is the run's own config version's list, when it has one.
 */
function inputRefusal(
  detail: TabularIngestJobDetail,
  accept: readonly string[] | undefined
): { reason: string; fix?: string; raw?: boolean } {
  const message = detail.error?.message ?? ''
  const version = `${detail.configName} v${detail.configVersion}`
  const again = detail.source.kind === 'intake' ? 're-run this file' : 'start a new run with it'
  switch (detail.error?.code) {
    case 'TABULAR_INPUT_EXTENSION': {
      const extension = tabularFileExtension(message)
      const kind = extension ? `is a ${extension} file` : 'has no file extension'
      const reason = accept?.length
        ? `${message} ${kind}, and ${version} accepts only ${extensionList(accept)}.`
        : `${message} ${kind}, which ${version} does not accept.`
      const buildable = (TABULAR_CONFIG_BUILD_ACCEPTED_EXTENSIONS as readonly string[]).includes(
        extension
      )
      return {
        reason,
        fix: buildable
          ? `Publish a version of ${detail.configName} that accepts ${extension}: open the config, choose Edit with agent, and attach an example ${extension} file. Then ${again}.`
          : `A tabular config cannot read ${extension || 'a file without an extension'}, so send the file in a type ${detail.configName} accepts.`
      }
    }
    case 'TABULAR_INPUT_FILE_COUNT':
      return {
        reason: `${message.charAt(0).toUpperCase()}${message.slice(1)}.`,
        fix: 'Split the files across runs.'
      }
    case 'TABULAR_INPUT_FILES_CONTAIN_DUPLICATE_NAMES':
      return {
        reason: 'Two of the files have the same name.',
        fix: 'Rename one and start a new run.'
      }
    case 'TABULAR_INPUT_FILE_NAME_INVALID':
      return {
        reason: 'A file name is empty or contains a slash, a backslash or a null character.',
        fix: 'Rename the file and start a new run.'
      }
    case 'TABULAR_INPUT_FILE_EMPTY':
      return { reason: 'A file is empty.', fix: 'Send the file again with its contents.' }
    default:
      return { reason: message || 'The run failed before it stored its files.', raw: !!message }
  }
}

/** `38 pages in 4m 10s (9.1/min) · `, from the extractor's own wall time. */
function pageReadsLabel(cost: TabularIngestJobDetail['cost']): string {
  const reads = cost?.pageReads
  if (!reads) return ''
  const minutes = reads.wallMs / 60_000
  const rate = reads.pages > 0 && minutes > 0 ? ` (${(reads.pages / minutes).toFixed(1)}/min)` : ''
  return `${plural(reads.pages, 'page', 'pages')} in ${formatDurationMs(reads.wallMs)}${rate} · `
}

function slotWaitLabel(cost: TabularIngestJobDetail['cost']): string {
  const waited = cost?.pageReads?.slotWaitMs ?? 0
  return waited > 0 ? ` · waited ${formatCallDurationMs(waited)} for slots` : ''
}

function discretionLabel(cost: TabularIngestJobDetail['cost']): string {
  const parts = [
    cost?.peeks ? plural(cost.peeks, 'page view', 'page views') : '',
    cost?.reextracts ? plural(cost.reextracts, 're-extraction', 're-extractions') : '',
    cost?.skippedPages ? plural(cost.skippedPages, 'page skipped', 'pages skipped') : ''
  ].filter(Boolean)
  return parts.map((part) => ` · ${part}`).join('')
}

function extractorLabel(cost: TabularIngestJobDetail['cost']): string {
  const usd = cost?.usd === undefined ? '' : ` · $${cost.usd.toFixed(2)}`
  return (
    `extractor · ${pageReadsLabel(cost)}${cost?.modelCalls ?? 0} model calls` +
    `${slotWaitLabel(cost)}${discretionLabel(cost)}${usd}`
  )
}

/** A refused commit's message, with the first failing rows when the reviewed rows failed validation. */
function commitRefusal(error: unknown): string {
  const message = extractErrorMessage(error, 'The run could not be committed.')
  const data = (error as { data?: { code?: unknown; details?: unknown } } | undefined)?.data
  if (data?.code !== REVIEW_VALIDATION_FAILED || !Array.isArray(data.details)) return message
  const details = data.details.filter((detail): detail is string => typeof detail === 'string')
  if (details.length === 0) return message
  return `${message}: ${details.slice(0, 3).join('; ')}${details.length > 3 ? '; …' : ''}`
}

function sourceLabel(source: TabularIngestJobDetail['source']): string {
  if (source.kind === 'upload') return `upload by ${source.userId}`
  if (source.kind === 'intake') return `intake · ${source.intakeName}`
  if (source.kind === 'config_examples') return `config examples · saved by ${source.userId}`
  return 'agent'
}

function workbenchHref(target: NonNullable<TabularIngestJobDetail['target']>): string {
  const params = new URLSearchParams({ [PLANT_COMPUTE_DB_QUERY_PARAM]: target.connectionId })
  return `${plantHref(target.connectionId, 'Database')}?${params.toString()}`
}

export default function TabularIngestJobPage() {
  const { jobId = '' } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  // Callers off the Runs list (an intake's ledger, say) name where Back
  // should return; the default is the Ingest landing.
  const backTo = (location.state as { from?: string } | null)?.from ?? '/ingest'
  // The config page's *Reject…* comes back here.
  const fromHere = { from: location.pathname }
  const [pollingStopped, setPollingStopped] = React.useState(false)
  React.useEffect(() => setPollingStopped(false), [jobId])
  const [parked, setParked] = React.useState(false)
  const detailQuery = useGetTabularIngestJobDetailQuery(jobId, {
    skip: !jobId,
    pollingInterval: !pollingStopped && parked ? PARKED_POLL_MS : 0,
    refetchOnMountOrArgChange: true
  })
  const detail = detailQuery.data
  const terminal = detail ? TERMINAL_STATES.has(detail.state) : false
  React.useEffect(() => {
    setPollingStopped(terminal)
  }, [terminal])
  const authzQuery = useGetAuthzMeQuery()
  const canManage = authzQuery.data?.permissions.includes('ingest/manage') === true
  const review = useRunPageReview(detail, canManage)
  const pendingBuildId = detail?.pendingDraft?.buildJobId ?? ''
  React.useEffect(() => {
    setParked(
      (detail?.state === 'awaiting_rebuild' && !pendingBuildId) ||
        HELD_STATES.has(detail?.state ?? '')
    )
  }, [detail?.state, pendingBuildId])
  const buildQuery = useGetTabularConfigBuildQuery(pendingBuildId, {
    skip: !pendingBuildId
  })
  React.useEffect(() => {
    if (
      detail?.state === 'awaiting_rebuild' &&
      (detail.pendingDraft?.version !== undefined ||
        buildQuery.data?.result.phase === 'awaiting_review')
    ) {
      setPollingStopped(true)
    }
  }, [buildQuery.data?.result.phase, detail?.pendingDraft?.version, detail?.state])
  const configQuery = useListIngestMappingConfigsQuery(
    {
      kind: 'mapped_tabular_ingest',
      name: detail?.configName ?? '',
      version: detail?.configVersion,
      limit: 1
    },
    { skip: !detail }
  )
  const latestConfigQuery = useListIngestMappingConfigsQuery(
    {
      kind: 'mapped_tabular_ingest',
      name: detail?.configName ?? '',
      latest: true,
      limit: 1
    },
    { skip: !detail }
  )
  const [acknowledge, acknowledgeState] = useAcknowledgeTabularIngestJobMutation()
  const [rerun, rerunState] = useRerunTabularIngestJobMutation()
  const [rebuild, rebuildState] = useRebuildTabularIngestConfigMutation()
  const [rejectDraft, rejectState] = useRejectTabularConfigDraftMutation()
  const [stop, stopState] = useStopIngestJobMutation()
  const [commit, commitState] = useCommitTabularIngestJobMutation()
  const [discard, discardState] = useDiscardTabularIngestJobMutation()
  const [stopOpen, setStopOpen] = React.useState(false)
  const [discardOpen, setDiscardOpen] = React.useState(false)
  const [badFileOpen, setBadFileOpen] = React.useState(false)
  const [rejectOpen, setRejectOpen] = React.useState(false)
  const [chooseOpen, setChooseOpen] = React.useState(false)
  const [note, setNote] = React.useState('')
  const [actionError, setActionError] = React.useState<string>()
  const [chosenConfig, setChosenConfig] = React.useState<SelectedTabularConfig>()
  const [confirmRerunConfig, setConfirmRerunConfig] = React.useState<
    SelectedTabularConfig | IngestMappingConfigSummary
  >()
  const [confirmHeldRerun, setConfirmHeldRerun] = React.useState<
    SelectedTabularConfig | IngestMappingConfigSummary
  >()
  const [replacing, setReplacing] = React.useState<ReplacementRequest & { retry: () => void }>()
  // Dialogs, notes and errors belong to the job they were opened on.
  React.useEffect(() => {
    setStopOpen(false)
    setDiscardOpen(false)
    setBadFileOpen(false)
    setRejectOpen(false)
    setChooseOpen(false)
    setNote('')
    setActionError(undefined)
    setChosenConfig(undefined)
    setConfirmRerunConfig(undefined)
    setConfirmHeldRerun(undefined)
    setReplacing(undefined)
  }, [jobId])

  if (detailQuery.isLoading || !detail) {
    return (
      <div className="mx-auto w-full max-w-6xl py-8 text-xs text-ink-3">
        {detailQuery.isError ? 'Tabular ingest job was not found.' : 'Loading tabular ingest…'}
      </div>
    )
  }

  const pill = tabularJobPill(
    detail.state,
    detail.acknowledged,
    detail.parserKind,
    detail.review?.commit !== undefined
  )
  const savedConfig = configQuery.data?.[0]
  const latestConfig = latestConfigQuery.data?.[0]
  // An intake keeps its own copy of the file, so its runs can always run again from it.
  const rerunRefusal =
    !detail.inputsStaged && detail.source.kind !== 'intake' ? UNSTAGED_RERUN_TITLE : undefined
  const build = buildQuery.data
  const buildPhase = build?.result.phase
  const draftVersion = detail.pendingDraft?.version ?? build?.proposedVersion
  const draftDigest = build?.result.draft?.draftDigest
  const editedPages = Object.keys(detail.review?.edited ?? {}).length
  const elapsed = formatRunDuration(detail.submittedAt, detail.completedAt)
  const fileLabel =
    detail.files.length > 1
      ? `${detail.files[0]?.name ?? 'Tabular input'} +${detail.files.length - 1}`
      : (detail.files[0]?.name ?? 'Tabular ingest')
  const meta = [
    `${detail.parserKind} parser`,
    detail.parserKind === 'script' && !detail.pendingDraft
      ? 'no model'
      : detail.pendingDraft
        ? 'builder ran (update mode)'
        : extractorLabel(detail.cost),
    `source: ${sourceLabel(detail.source)}`,
    ...(detail.submittedAt ? [`started ${new Date(detail.submittedAt).toLocaleString()}`] : []),
    ...(elapsed !== '—' ? [elapsed] : [])
  ]

  const runWithConfig = async (
    config: SelectedTabularConfig | IngestMappingConfigSummary,
    replaceExisting = false
  ): Promise<void> => {
    setActionError(undefined)
    try {
      const next = await rerun({
        jobId,
        configName: config.name,
        configVersion: config.version,
        ...(replaceExisting ? { replaceExisting: true } : {})
      }).unwrap()
      // The rerun's page keeps the caller's Back target.
      navigate(tabularJobPath(next.jobId), { state: location.state })
    } catch (error) {
      const replacement = replacementRequestOf(error)
      if (replacement) {
        setReplacing({ ...replacement, retry: () => void runWithConfig(config, true) })
        return
      }
      setActionError(extractErrorMessage(error, 'The action could not be completed.'))
    }
  }

  const requestRerun = (
    config: SelectedTabularConfig | IngestMappingConfigSummary | undefined,
    discardConfirmed = false
  ) => {
    if (!config) return
    if (!discardConfirmed && HELD_STATES.has(detail.state)) {
      setConfirmHeldRerun(config)
      return
    }
    const hasUpsert = config.definition.output.tables.some((table) => table.mode === 'upsert')
    if (hasUpsert) {
      setConfirmRerunConfig(config)
      return
    }
    void runWithConfig(config)
  }

  const stopRun = async () => {
    setActionError(undefined)
    // Drops an upload still in flight from this tab; the server stops the job either way.
    if (detail.state === 'uploading') abortIngestUpload(jobId)
    try {
      await stop(jobId).unwrap()
    } catch (error) {
      setActionError(extractErrorMessage(error, 'The run could not be stopped.'))
    }
    setStopOpen(false)
    void detailQuery.refetch()
  }

  const commitRun = async (replaceExisting = false) => {
    setActionError(undefined)
    try {
      await commit({ jobId, ...(replaceExisting ? { replaceExisting: true } : {}) }).unwrap()
    } catch (error) {
      const replacement = replacementRequestOf(error)
      if (replacement) {
        setReplacing({ ...replacement, retry: () => void commitRun(true) })
        return
      }
      setActionError(commitRefusal(error))
    }
  }

  const rebuildConfig = async () => {
    const note = detail.error?.message || 'Operator requested a parser rebuild'
    setActionError(undefined)
    try {
      const result = await rebuild({ jobId, note }).unwrap()
      navigate(tabularConfigBuildPath(detail.configName, result.buildJobId), { state: fromHere })
    } catch (error) {
      setActionError(extractErrorMessage(error, 'The action could not be completed.'))
    }
  }

  return (
    <div className={COMPACT_PAGE}>
      <div className="sticky top-12 z-[5] -mt-4 flex min-w-0 flex-col gap-1.5 border-b border-border bg-background pt-2 pb-2.5 md:-mt-6">
        <Button asChild variant="ghost" size="sm" className="-ml-2 h-6 w-fit px-1.5 text-11">
          <Link to={backTo}>
            <ArrowLeft className="size-3" />
            Back to ingest
          </Link>
        </Button>
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
          <StatusPill status={pill.status}>{pill.label}</StatusPill>
          <h1 className="min-w-0 flex-1 truncate text-base font-semibold" title={fileLabel}>
            {fileLabel}
          </h1>
          <div className="flex flex-wrap items-center gap-1.5">
            {STOPPABLE_STATES.has(detail.state) || detail.state === 'cancelling' ? (
              <WriteButton
                canManage={canManage}
                size="sm"
                variant="outline"
                className={cn(
                  COMPACT_ACTION,
                  'border-st-down/40 text-st-down hover:bg-st-down-bg hover:text-st-down'
                )}
                disabled={detail.state === 'cancelling' || stopState.isLoading}
                onClick={() => setStopOpen(true)}
              >
                {detail.state === 'cancelling' ? 'Stopping…' : 'Stop'}
              </WriteButton>
            ) : null}
            <WriteButton
              canManage={canManage}
              size="sm"
              variant="outline"
              className={cn(COMPACT_ACTION, rerunRefusal && 'disabled:pointer-events-auto')}
              disabled={
                !latestConfig ||
                !RERUNNABLE_STATES.has(detail.state) ||
                !!rerunRefusal ||
                rerunState.isLoading
              }
              title={rerunRefusal}
              onClick={() => requestRerun(latestConfig)}
            >
              Re-run
            </WriteButton>
            <Button asChild size="sm" variant="outline" className={COMPACT_ACTION}>
              <Link to={tabularConfigPath(detail.configName)}>Open config</Link>
            </Button>
          </div>
        </div>
        <p className={cn(META_TEXT, 'truncate')} title={meta.join(' · ')}>
          <Link
            to={tabularConfigPath(detail.configName, detail.configVersion)}
            className="hover:underline"
          >
            {detail.configName} @v{detail.configVersion}
          </Link>
          {meta.map((part, index) => (
            <span key={index}> · {part}</span>
          ))}
        </p>
      </div>

      <StateBanner
        detail={detail}
        buildJobId={pendingBuildId}
        buildPhase={buildPhase}
        draftVersion={draftVersion}
        canManage={canManage}
        onReject={() => {
          setActionError(undefined)
          setNote('')
          setRejectOpen(true)
        }}
        onBadFile={() => {
          setActionError(undefined)
          setNote('')
          setBadFileOpen(true)
        }}
        onRebuild={() => void rebuildConfig()}
        onChoose={() => {
          setActionError(undefined)
          setChooseOpen(true)
        }}
        onRerun={() => requestRerun(latestConfig)}
        onCommit={() => void commitRun()}
        onDiscard={() => {
          setActionError(undefined)
          setNote('')
          setDiscardOpen(true)
        }}
        rerunAvailable={!!latestConfig && !rerunRefusal}
        rerunning={rerunState.isLoading}
        rebuilding={rebuildState.isLoading}
        committing={commitState.isLoading}
        unsavedEdits={review?.edits.dirty === true}
        configAccept={savedConfig?.definition.input?.accept}
      />
      {actionError ? (
        <Banner tone="down" title="Action failed">
          {actionError}
        </Banner>
      ) : null}

      {detail.state !== 'awaiting_rebuild' ? (
        <WhatCameOut detail={detail} config={savedConfig} review={review} />
      ) : null}
      <ChecksCard detail={detail} />
      <InputCard detail={detail} />
      <ToolCallsCard detail={detail} />
      <LogsCard detail={detail} />

      <NoteDialog
        open={badFileOpen}
        onOpenChange={setBadFileOpen}
        title="Mark this as a bad file?"
        description="The failed run remains in history and is marked ignored."
        note={note}
        onNoteChange={setNote}
        actionLabel="Ignore bad file"
        loading={acknowledgeState.isLoading}
        error={actionError}
        onSubmit={() => {
          void acknowledge({ jobId, ...(note.trim() ? { note: note.trim() } : {}) })
            .unwrap()
            .then(() => {
              setActionError(undefined)
              setBadFileOpen(false)
              void detailQuery.refetch()
            })
            .catch((error) =>
              setActionError(extractErrorMessage(error, 'The action could not be completed.'))
            )
        }}
      />
      <NoteDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Discard this run?"
        description={`Nothing is committed, and this run can't be committed later. Its pages and extracted rows stay here to look at.${editedPages ? ` The row edits on ${plural(editedPages, 'page', 'pages')} are not committed either.` : ''}`}
        note={note}
        onNoteChange={setNote}
        actionLabel="Discard run"
        destructive
        loading={discardState.isLoading}
        error={actionError}
        onSubmit={() => {
          void discard({ jobId, ...(note.trim() ? { note: note.trim() } : {}) })
            .unwrap()
            .then(() => {
              setActionError(undefined)
              setDiscardOpen(false)
            })
            .catch((error) =>
              setActionError(extractErrorMessage(error, 'The run could not be discarded.'))
            )
        }}
      />
      <NoteDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Reject this config draft?"
        description="The held ingest job will fail and nothing will be written."
        note={note}
        onNoteChange={setNote}
        actionLabel="Reject draft"
        required
        loading={rejectState.isLoading}
        error={actionError}
        onSubmit={() => {
          if (!pendingBuildId || !draftDigest || !note.trim()) return
          void rejectDraft({
            buildJobId: pendingBuildId,
            draftDigest,
            note: note.trim()
          })
            .unwrap()
            .then(() => {
              setActionError(undefined)
              setRejectOpen(false)
              void detailQuery.refetch()
            })
            .catch((error) =>
              setActionError(extractErrorMessage(error, 'The action could not be completed.'))
            )
        }}
      />
      <AlertDialog
        open={stopOpen}
        onOpenChange={(open) => {
          if (!stopState.isLoading) setStopOpen(open)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop this run?</AlertDialogTitle>
            <AlertDialogDescription>
              {detail.parserKind === 'agent'
                ? 'The agent stops at its next step, and the pages it has extracted so far are discarded.'
                : 'The parser stops at its next step, and its output so far is discarded.'}{' '}
              Tables already written stay written; nothing is rolled back. Re-run to start over.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={stopState.isLoading}>Keep running</AlertDialogCancel>
            <AlertDialogAction
              variant="outline"
              className="border-st-down bg-st-down text-white shadow-sm hover:bg-st-down/90 hover:text-white dark:border-st-down dark:bg-st-down dark:text-white dark:hover:bg-st-down/90 dark:hover:text-white"
              disabled={stopState.isLoading}
              onClick={(event) => {
                event.preventDefault()
                void stopRun()
              }}
            >
              Stop run
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={chooseOpen} onOpenChange={setChooseOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Choose another config</DialogTitle>
            <DialogDescription>
              The same durable input files will run as a new job with the version you choose.
            </DialogDescription>
          </DialogHeader>
          <ConfigPicker
            value={
              chosenConfig
                ? {
                    id: chosenConfig.id,
                    name: chosenConfig.name,
                    version: chosenConfig.version
                  }
                : undefined
            }
            onSelect={setChosenConfig}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setChooseOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!chosenConfig || rerunState.isLoading}
              onClick={() => {
                setChooseOpen(false)
                requestRerun(chosenConfig)
              }}
            >
              Run with this config
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={!!confirmHeldRerun}
        onOpenChange={(open) => {
          if (!open) setConfirmHeldRerun(undefined)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this run and re-run it?</AlertDialogTitle>
            <AlertDialogDescription>
              This run has not committed. Re-running discards it, page decisions and all, and
              extracts the files again as a new run that is reviewed on its own.
              {editedPages
                ? ` The row edits on ${plural(editedPages, 'page', 'pages')} are discarded too and not carried over to the new run.`
                : ''}
              {detail.review?.inOrder
                ? ' Later files from its intake wait for the new run instead.'
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel size="sm">Keep this run</AlertDialogCancel>
            <AlertDialogAction
              size="sm"
              onClick={() => {
                const config = confirmHeldRerun
                setConfirmHeldRerun(undefined)
                requestRerun(config, true)
              }}
            >
              Discard and re-run
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <UpsertConfirmDialog
        open={!!confirmRerunConfig}
        onOpenChange={(open) => {
          if (!open) setConfirmRerunConfig(undefined)
        }}
        tables={(confirmRerunConfig?.definition.output.tables ?? [])
          .filter((table) => table.mode === 'upsert')
          .map((table) => ({ table: table.table, key: table.key }))}
        onConfirm={() => {
          const config = confirmRerunConfig
          setConfirmRerunConfig(undefined)
          if (config) void runWithConfig(config)
        }}
      />
      <ReplaceConfirmDialog
        open={!!replacing}
        onOpenChange={(open) => {
          if (!open) setReplacing(undefined)
        }}
        configName={replacing?.configName ?? ''}
        files={replacing?.files ?? []}
        onConfirm={() => {
          const retry = replacing?.retry
          setReplacing(undefined)
          retry?.()
        }}
      />
    </div>
  )
}

function StateBanner({
  detail,
  buildJobId,
  buildPhase,
  draftVersion,
  canManage,
  onReject,
  onBadFile,
  onRebuild,
  onChoose,
  onRerun,
  onCommit,
  onDiscard,
  rerunAvailable,
  rerunning,
  rebuilding,
  committing,
  unsavedEdits,
  configAccept
}: {
  detail: TabularIngestJobDetail
  /** The pending rebuild's job, when the hold has one: the draft lives on its page. */
  buildJobId?: string
  buildPhase?: string
  draftVersion?: number
  canManage: boolean
  onReject: () => void
  onBadFile: () => void
  onRebuild: () => void
  onChoose: () => void
  onRerun: () => void
  onCommit: () => void
  onDiscard: () => void
  rerunAvailable: boolean
  rerunning: boolean
  rebuilding: boolean
  committing: boolean
  /** A page has row edits not saved yet, so committing would leave them out. */
  unsavedEdits: boolean
  /** The file types the run's config version accepts; unset when it takes any. */
  configAccept?: readonly string[]
}) {
  const location = useLocation()
  // The config page's *Reject…* comes back here.
  const fromHere = { from: location.pathname }
  const review = detail.review
  const target = detail.target?.connectionName ?? detail.configName
  if (detail.state === 'awaiting_review') {
    return (
      <ReviewBanner
        detail={detail}
        canManage={canManage}
        committing={committing}
        unsavedEdits={unsavedEdits}
        onCommit={onCommit}
        onDiscard={onDiscard}
      />
    )
  }
  if (detail.state === 'awaiting_turn') {
    return (
      <WaitingBanner
        detail={detail}
        canManage={canManage}
        committing={committing}
        unsavedEdits={unsavedEdits}
        onCommit={onCommit}
        onDiscard={onDiscard}
      />
    )
  }
  const rows = detail.tables.reduce(
    (sum, table) => sum + (table.status === 'written' ? table.rowCount : 0),
    0
  )
  if (detail.state === 'done' && review?.unreviewed) {
    const flagged = (detail.pages ?? []).filter((page) => page.status === 'needs_review').length
    return (
      <Banner tone="warn" title={`Committed ${plural(rows, 'row', 'rows')} without review`}>
        <p>
          The extracted rows could not be kept for review, so this run committed them as they were:
          rows from its {plural(flagged, 'flagged page were', 'flagged pages were')} written to{' '}
          {target} with needs_review = true. Check them there, or re-run the file to review it.
        </p>
      </Banner>
    )
  }
  if (detail.state === 'done') {
    if (!review?.commit) return null
    const decisions = Object.values(review.pages)
    const excluded = decisions.filter((page) => page.decision === 'excluded').length
    const committedBy = review.commit.by
      ? `committed by ${review.commit.by}`
      : 'committed on its own once the earlier files from its intake settled'
    return (
      <Banner tone="run" title={`Committed ${plural(rows, 'row', 'rows')} to ${target}`}>
        <p>
          {decisions.length > 0
            ? `${plural(decisions.length - excluded, 'flagged page', 'flagged pages')} reviewed${excluded ? `, ${excluded} excluded` : ''} · `
            : ''}
          {committedBy} {formatRelativeTime(review.commit.at)}.
          {decisions.length > 0
            ? ` Reviewed rows were written with needs_review = false${excluded ? ', and the excluded pages’ rows were left out' : ''}.`
            : ''}
        </p>
      </Banner>
    )
  }
  if (detail.state === 'uploading' || detail.state === 'processing') {
    const latest = (detail.latestLog ?? detail.logTail.at(-1))?.message.split('\n', 1)[0]
    const reviewedCommit = review?.commit && Object.keys(review.pages).length > 0
    return (
      <Banner
        tone="info"
        title={
          reviewedCommit
            ? 'Committing the reviewed rows'
            : review?.commit
              ? 'Committing'
              : 'Running'
        }
      >
        <span className="line-clamp-2 break-words [overflow-wrap:anywhere]">
          {latest || (detail.parserKind === 'agent' ? 'Running the agent.' : 'Running the parser.')}
        </span>
      </Banner>
    )
  }
  if (detail.state === 'awaiting_rebuild') {
    const diffs = detail.files.flatMap((file) =>
      file.fingerprint?.matched === false ? file.fingerprint.diff : []
    )
    const ready = buildPhase === 'awaiting_review'
    return (
      <Banner tone="warn" title="Structure changed — nothing was written">
        {diffs.length ? (
          <ul className={cn(MONO_WRAP, 'space-y-1 text-11')}>
            {diffs.map((diff, index) => (
              <li key={`${diff.path}-${index}`}>
                {diff.path} expected {JSON.stringify(diff.expected)} → found{' '}
                {JSON.stringify(diff.found)}
              </li>
            ))}
          </ul>
        ) : (
          <p>The input fingerprint no longer matches this config.</p>
        )}
        {detail.pendingDraft?.attached ? (
          <p className="mt-1.5">
            This run joined a config update that was already open. It runs again when that draft is
            saved, and fails if the draft is rejected.
          </p>
        ) : null}
        <div className="mt-2.5 flex flex-wrap gap-2">
          {ready && draftVersion ? (
            <>
              <Button asChild size="sm" className={COMPACT_ACTION}>
                <Link
                  to={
                    buildJobId
                      ? tabularConfigBuildPath(detail.configName, buildJobId)
                      : tabularConfigPath(detail.configName)
                  }
                  state={fromHere}
                >
                  Review draft v{draftVersion}
                </Link>
              </Button>
              <WriteButton
                canManage={canManage}
                size="sm"
                variant="outline"
                className={COMPACT_ACTION}
                onClick={onReject}
              >
                Reject…
              </WriteButton>
            </>
          ) : (
            <StatusPill status="info">building</StatusPill>
          )}
        </div>
      </Banner>
    )
  }
  if (detail.state === 'cancelling') {
    return (
      <Banner tone="info" title="Stopping">
        The run stops at its next step.
      </Banner>
    )
  }
  if (detail.state === 'cancelled') {
    // Tables commit one at a time, so a stop during the write keeps the ones already done.
    const written = detail.tables.filter((table) => table.status === 'written')
    return (
      <Banner
        tone="idle"
        title={
          written.length === 0
            ? 'Stopped — nothing was written'
            : `Stopped — ${written.length} of ${detail.tables.length} tables written`
        }
      >
        {written.length > 0 ? (
          <p>
            Written before the stop: {written.map((table) => table.name).join(', ')}. Nothing was
            rolled back.
          </p>
        ) : null}
        <p>Re-run to start over.</p>
      </Banner>
    )
  }
  if (detail.state === 'skipped' && review?.discard) {
    return (
      <Banner tone="idle" title="Discarded — nothing was committed">
        <p>
          Discarded by {review.discard.by} {formatRelativeTime(review.discard.at)}
          {review.discard.note ? ` · ${review.discard.note}` : ''}.
        </p>
      </Banner>
    )
  }
  if (detail.state === 'skipped') {
    return <Banner tone="idle" title="Skipped — an identical file was already ingested." />
  }
  if (detail.state === 'error' && detail.acknowledged) {
    return (
      <Banner tone="idle" title="Failed and ignored">
        <p>{detail.error?.message}</p>
        <p>Marked as a bad file · {detail.acknowledgeNote || 'no note'}</p>
      </Banner>
    )
  }
  // The file never reached a parser, so there is nothing to rebuild or mark as
  // bad; the fix is on the intake.
  if (detail.state === 'error' && detail.error?.code === CONFIG_ARCHIVED_ERROR) {
    return (
      <Banner tone="down" title="Failed — nothing was written">
        <p>{detail.error.message}</p>
      </Banner>
    )
  }
  // A run with no stored files failed before any parser read them, whatever
  // the code, so nothing is known about the file's layout or values.
  if (
    detail.state === 'error' &&
    (INPUT_REFUSED_ERRORS.has(detail.error?.code ?? '') || !detail.inputsStaged)
  ) {
    const refusal = inputRefusal(detail, configAccept)
    return (
      <Banner tone="down" title="Failed — the file never reached the parser">
        <p className={refusal.raw ? cn(MONO_WRAP, 'text-11') : undefined}>{refusal.reason}</p>
        <p>Nothing was read or written, so the file's layout and values were not checked.</p>
        {refusal.fix ? <p>{refusal.fix}</p> : null}
        <div className="mt-2.5 flex flex-wrap gap-2">
          <WriteButton
            canManage={canManage}
            size="sm"
            variant="outline"
            className={COMPACT_ACTION}
            onClick={onBadFile}
          >
            Bad file, ignore
          </WriteButton>
          <Button asChild size="sm" variant="outline" className={COMPACT_ACTION}>
            <Link to={tabularConfigPath(detail.configName)}>Edit the config</Link>
          </Button>
        </div>
      </Banner>
    )
  }
  if (detail.state === 'error' && review?.commit) {
    const written = detail.tables.filter((table) => table.status === 'written')
    return (
      <Banner
        tone="down"
        title={
          written.length === 0
            ? 'Commit failed — nothing was written'
            : `Commit failed — ${written.length} of ${detail.tables.length} tables written`
        }
      >
        <p className={cn(MONO_WRAP, 'text-11')}>{detail.error?.message}</p>
        {written.length > 0 ? (
          <p>Written: {written.map((table) => table.name).join(', ')}. Nothing was rolled back.</p>
        ) : null}
        <p>Re-run to extract the files again; the new run is reviewed on its own.</p>
        <div className="mt-2.5 flex flex-wrap gap-2">
          <WriteButton
            canManage={canManage}
            size="sm"
            className={COMPACT_ACTION}
            disabled={!rerunAvailable || rerunning}
            onClick={onRerun}
          >
            Re-run
          </WriteButton>
        </div>
      </Banner>
    )
  }
  if (detail.state === 'error' && detail.parserKind === 'agent') {
    const failedPage = detail.pages?.find(
      (page) => page.status === 'mismatch' || page.status === 'unreadable'
    )
    return (
      <Banner
        tone="down"
        title={
          failedPage
            ? 'Failed — a page does not match this config. Nothing was written.'
            : 'Failed — nothing was written'
        }
      >
        <p className={cn(MONO_WRAP, 'text-11')}>
          {failedPage
            ? `Page ${failedPage.pageNumber} — ${failedPage.reason || failedPage.status}`
            : detail.error?.message}
        </p>
        <p>
          Agent configs have no fingerprint, so the platform cannot distinguish a redesigned form
          from a stray document automatically.
        </p>
        <div className="mt-2.5 flex flex-wrap gap-2">
          {detail.inputsStaged ? (
            <WriteButton
              canManage={canManage}
              size="sm"
              className={COMPACT_ACTION}
              onClick={onChoose}
            >
              Choose another config
            </WriteButton>
          ) : null}
          <WriteButton
            canManage={canManage}
            size="sm"
            variant="outline"
            className={COMPACT_ACTION}
            disabled={!rerunAvailable || rerunning}
            onClick={onRerun}
          >
            Re-run
          </WriteButton>
          {detail.source.kind === 'intake' ? (
            <WriteButton
              canManage={canManage}
              size="sm"
              variant="outline"
              className={COMPACT_ACTION}
              onClick={onBadFile}
            >
              Bad file, ignore
            </WriteButton>
          ) : null}
        </div>
      </Banner>
    )
  }
  if (detail.state === 'error') {
    const fingerprintMismatch = detail.files.some((file) => file.fingerprint?.matched === false)
    // Tables are committed one at a time with no rollback, so a late failure
    // leaves the earlier tables written; say so rather than "nothing".
    const written = detail.tables.filter((table) => table.status === 'written')
    const unwritten = detail.tables.filter((table) => table.status !== 'written')
    const outcome =
      written.length === 0
        ? 'nothing was written'
        : `${written.length} of ${detail.tables.length} tables written`
    return (
      <Banner
        tone="down"
        title={fingerprintMismatch ? `Config update failed — ${outcome}` : `Failed — ${outcome}`}
      >
        <p className={cn(MONO_WRAP, 'text-11')}>{detail.error?.message}</p>
        {written.length > 0 ? (
          <p>
            Written: {written.map((table) => table.name).join(', ')}. Not written:{' '}
            {unwritten.map((table) => table.name).join(', ')}.
          </p>
        ) : null}
        <p>
          {fingerprintMismatch
            ? `The input layout did not match v${detail.configVersion}, and the config update could not complete.`
            : detail.source.kind === 'config_examples'
              ? `These are the examples v${detail.configVersion} was built from. This can be a bad value or a validation rule that is too strict.`
              : `The layout matched v${detail.configVersion}, so no agent ran. This can be a bad value or a validation rule that is too strict.`}
        </p>
        <div className="mt-2.5 flex flex-wrap gap-2">
          {/* A rebuild learns from the run's stored files. */}
          {detail.inputsStaged ? (
            <WriteButton
              canManage={canManage}
              size="sm"
              className={COMPACT_ACTION}
              onClick={onRebuild}
              disabled={rebuilding}
            >
              Rebuild with agent
            </WriteButton>
          ) : null}
          <WriteButton
            canManage={canManage}
            size="sm"
            variant="outline"
            className={COMPACT_ACTION}
            onClick={onBadFile}
          >
            Bad file, ignore
          </WriteButton>
          <Button asChild size="sm" variant="outline" className={COMPACT_ACTION}>
            <Link to={tabularConfigPath(detail.configName, { tab: 'checks' })}>
              Edit the config
            </Link>
          </Button>
        </div>
      </Banner>
    )
  }
  return null
}

const UNSAVED_EDITS_TITLE = 'Save or discard the row edits on the page first'

/**
 * A run held for review: how many flagged pages are decided, and Commit once
 * all of them are. Nothing is written before then.
 */
function ReviewBanner({
  detail,
  canManage,
  committing,
  unsavedEdits,
  onCommit,
  onDiscard
}: {
  detail: TabularIngestJobDetail
  canManage: boolean
  committing: boolean
  unsavedEdits: boolean
  onCommit: () => void
  onDiscard: () => void
}) {
  const flagged = (detail.pages ?? []).filter((page) => page.status === 'needs_review')
  const decisions = detail.review?.pages ?? {}
  const reviewed = flagged.filter((page) => decisions[page.pageId]?.decision === 'reviewed').length
  const excluded = flagged.filter((page) => decisions[page.pageId]?.decision === 'excluded').length
  const remaining = flagged.length - reviewed - excluded
  const editedPages = Object.keys(detail.review?.edited ?? {}).length
  const target = detail.target?.connectionName ?? detail.configName
  const share = (count: number) => `${flagged.length ? (count / flagged.length) * 100 : 0}%`
  return (
    <Banner
      tone={remaining === 0 ? 'info' : 'warn'}
      title={remaining === 0 ? 'Ready to commit' : 'Held for review · nothing committed yet'}
    >
      <p>
        {remaining === 0
          ? `Every flagged page is reviewed or excluded. Nothing has been written to ${target} yet.`
          : `${flagged.length} of ${plural(detail.pages?.length ?? 0, 'page', 'pages')} need review. Check each flagged page against its image, then mark it reviewed or exclude it. The run commits once every flagged page is decided.`}
      </p>
      {detail.turn ? (
        <p>
          Its intake commits files in the order they arrived, so committing queues this run until{' '}
          <TurnLink turn={detail.turn} /> is committed or discarded.
        </p>
      ) : null}
      <div className="flex flex-col gap-1 text-11">
        <div className="flex flex-wrap justify-between gap-2">
          <span>
            {reviewed + excluded} of {plural(flagged.length, 'flagged page', 'flagged pages')} done
            {editedPages ? ` · rows edited on ${plural(editedPages, 'page', 'pages')}` : ''}
          </span>
          {excluded ? (
            <span>
              {reviewed} reviewed · {excluded} excluded
            </span>
          ) : null}
        </div>
        <div
          role="progressbar"
          aria-label="Flagged pages decided"
          aria-valuemin={0}
          aria-valuemax={flagged.length}
          aria-valuenow={reviewed + excluded}
          className="flex h-1.5 overflow-hidden rounded-full bg-background/70"
        >
          <div className="bg-st-info" style={{ width: share(reviewed) }} />
          <div className="bg-ink-3" style={{ width: share(excluded) }} />
        </div>
      </div>
      <div className="mt-2.5 flex flex-wrap gap-2">
        <WriteButton
          canManage={canManage}
          size="sm"
          className={COMPACT_ACTION}
          disabled={remaining > 0 || committing || unsavedEdits}
          title={unsavedEdits ? UNSAVED_EDITS_TITLE : undefined}
          onClick={onCommit}
        >
          {committing
            ? 'Committing…'
            : remaining > 0
              ? `Commit (${plural(remaining, 'page', 'pages')} left)`
              : 'Commit'}
        </WriteButton>
        <WriteButton
          canManage={canManage}
          size="sm"
          variant="outline"
          className={cn(COMPACT_ACTION, DESTRUCTIVE_ACTION, DESTRUCTIVE_OUTLINE)}
          disabled={committing}
          onClick={onDiscard}
        >
          Discard run
        </WriteButton>
      </div>
    </Banner>
  )
}

/**
 * A run whose rows are ready while an earlier file from its intake is
 * unsettled. With no earlier file left, the release that should have
 * committed it was missed, so the reader can commit it now.
 */
function WaitingBanner({
  detail,
  canManage,
  committing,
  unsavedEdits,
  onCommit,
  onDiscard
}: {
  detail: TabularIngestJobDetail
  canManage: boolean
  committing: boolean
  unsavedEdits: boolean
  onCommit: () => void
  onDiscard: () => void
}) {
  const { turn } = detail
  const queued = detail.review?.queued
  const target = detail.target?.connectionName ?? detail.configName
  return (
    <Banner
      tone="info"
      title={
        turn ? `Waiting for ${turn.first.fileName}` : 'Ready to commit · nothing committed yet'
      }
    >
      {turn ? (
        <p>
          The rows are ready, but this intake commits files in the order they arrived. This run
          commits on its own once <TurnLink turn={turn} /> is committed or discarded. Nothing has
          been written to {target} yet.
        </p>
      ) : (
        <p>
          Every earlier file from this intake has settled, but the run has not started committing.
          Commit it now. Nothing has been written to {target} yet.
        </p>
      )}
      {queued?.by ? (
        <p className="text-11">
          Commit queued by {queued.by} {formatRelativeTime(queued.at)}.
        </p>
      ) : null}
      <div className="mt-2.5 flex flex-wrap gap-2">
        {turn ? null : (
          <WriteButton
            canManage={canManage}
            size="sm"
            className={COMPACT_ACTION}
            disabled={committing || unsavedEdits}
            title={unsavedEdits ? UNSAVED_EDITS_TITLE : undefined}
            onClick={onCommit}
          >
            {committing ? 'Committing…' : 'Commit now'}
          </WriteButton>
        )}
        <WriteButton
          canManage={canManage}
          size="sm"
          variant="outline"
          className={cn(COMPACT_ACTION, DESTRUCTIVE_ACTION, DESTRUCTIVE_OUTLINE)}
          disabled={committing}
          onClick={onDiscard}
        >
          Discard run
        </WriteButton>
      </div>
    </Banner>
  )
}

/** The earlier file a run waits for, linked to its run, and how many more come before this one. */
function TurnLink({ turn }: { turn: NonNullable<TabularIngestJobDetail['turn']> }) {
  const location = useLocation()
  const more = turn.ahead - 1
  return (
    <>
      <Link
        to={tabularJobPath(turn.first.jobId)}
        state={location.state}
        className="font-mono underline-offset-2 hover:underline"
      >
        {turn.first.fileName}
      </Link>
      {more > 0 ? ` and ${plural(more, 'more earlier file', 'more earlier files')}` : ''}
    </>
  )
}

function Banner({
  tone,
  title,
  children
}: {
  tone: 'info' | 'warn' | 'down' | 'idle' | 'run'
  title: string
  children?: React.ReactNode
}) {
  const classes = {
    run: 'border-st-run/30 bg-st-run-bg text-st-run',
    info: 'border-st-info/30 bg-st-info-bg text-st-info',
    warn: 'border-st-warn/30 bg-st-warn-bg text-st-warn',
    down: 'border-st-down/30 bg-st-down-bg text-st-down',
    idle: 'border-line bg-bg-sunk text-ink-2'
  }
  return (
    <section className={`min-w-0 rounded-lg border px-3 py-2.5 ${classes[tone]}`}>
      <h2 className="text-xs font-semibold">{title}</h2>
      {children ? <div className="mt-1.5 space-y-1.5 text-xs leading-5">{children}</div> : null}
    </section>
  )
}

function WhatCameOut({
  detail,
  config,
  review
}: {
  detail: TabularIngestJobDetail
  config?: IngestMappingConfigSummary
  review?: RunPageReview
}) {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const pages = detail.pages
  const hasPages = (pages?.length ?? 0) > 0
  const editedPages = Object.keys(detail.review?.edited ?? {}).length
  const tabs = [...(hasPages ? ['pages'] : []), ...detail.tables.map((table) => table.name)]
  const requested = searchParams.get('tab')
  const tab = requested && tabs.includes(requested) ? requested : tabs[0]
  const showView = React.useCallback(
    (view: TabularJobView, push = false) =>
      setSearchParams(tabularJobViewParams(view), { replace: !push, state: location.state }),
    [location.state, setSearchParams]
  )
  // A table row opens its page in the Pages tab, highlighted; Back returns to the table.
  const pageLinks = React.useMemo(() => {
    const byUrn = new Map(
      (pages ?? []).flatMap((page) => (page.sourcePageUrn ? [[page.sourcePageUrn, page]] : []))
    )
    const links = new Map<string, PageLinks>()
    if (byUrn.size === 0) return links
    for (const table of detail.tables) {
      links.set(table.name, {
        byUrn,
        open: (pageId, position) =>
          showView({ tab: 'pages', page: pageId, row: { table: table.name, position } }, true)
      })
    }
    return links
  }, [pages, detail.tables, showView])
  if (!tab) {
    return <Card className="p-3 text-xs text-ink-3">No output tables were produced.</Card>
  }
  return (
    <Card className="min-w-0 gap-3 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className={SECTION_LABEL}>Extracted rows</h2>
        <span className="text-11 text-ink-3">
          {detail.state === 'done'
            ? `committed${detail.target ? ` to ${detail.target.connectionName}` : ''}`
            : detail.state === 'error'
              ? 'partial — nothing committed'
              : detail.state === 'awaiting_review'
                ? 'held, not committed'
                : detail.state === 'awaiting_turn'
                  ? 'ready, waiting to commit'
                  : detail.review?.discard
                    ? 'discarded, nothing committed'
                    : 'not committed'}
        </span>
      </div>
      <Tabs
        value={tab}
        onValueChange={(value) =>
          showView({ tab: value, page: searchParams.get('page') ?? undefined })
        }
        className="min-w-0 gap-3"
      >
        <TabsList
          variant="line"
          className="h-auto max-w-full flex-wrap justify-start border-b border-line"
        >
          {hasPages ? (
            <TabsTrigger value="pages" className="h-7 flex-none px-2.5 text-xs">
              Pages
            </TabsTrigger>
          ) : null}
          {detail.tables.map((table) => (
            <TabsTrigger
              key={table.name}
              value={table.name}
              className="h-7 flex-none gap-1.5 px-2.5 text-xs"
            >
              {table.name}
              <span className="font-normal text-ink-3 tabular-nums">{table.rowCount}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        {pages && hasPages ? (
          <TabsContent value="pages" className="min-w-0">
            <PagesSummary pages={pages} />
            <PagesWorkspace
              jobId={detail.id}
              pages={pages}
              selectedPageId={searchParams.get('page') ?? undefined}
              highlight={parseTabularRowRef(searchParams.get('row'))}
              review={review}
              onSelect={(pageId) => showView({ tab: 'pages', page: pageId })}
              openAsPage={(pageId, filter) => (
                <Link
                  to={tabularJobPagePath(detail.id, pageId, filter === 'todo' ? 'todo' : undefined)}
                  state={location.state}
                  className="inline-flex items-center gap-1 text-11 text-st-info hover:underline"
                >
                  Open as page <ExternalLink className="size-3" />
                </Link>
              )}
            />
          </TabsContent>
        ) : null}
        {detail.tables.map((table) => {
          const declared = config?.definition.output.tables.find(
            (item) => item.table === table.name
          )
          const columns: ExtractedColumn[] =
            declared?.columns ??
            (table.previewRows[0] ?? []).map((_, index) => ({
              name: `col ${index + 1}`,
              type: 'VARCHAR'
            }))
          return (
            <TabsContent key={table.name} value={table.name} className="min-w-0">
              {editedPages ? (
                <p className="mb-2 text-11 text-ink-3">
                  Rows on {plural(editedPages, 'page was', 'pages were')} edited in review. This
                  table shows the rows as the run extracted them; open a page to see its edits
                  {review?.open ? ', which apply when the run commits' : ''}.
                </p>
              ) : null}
              <ExtractedRowsTable
                jobId={detail.id}
                table={table}
                columns={columns}
                pageLinks={pageLinks.get(table.name)}
              />
              <div className="mt-2.5 flex flex-wrap items-center gap-2 text-11 text-ink-3">
                <StatusPill
                  status={
                    table.status === 'failed' ? 'down' : table.status === 'written' ? 'run' : 'idle'
                  }
                >
                  {table.status}
                </StatusPill>
                <span>
                  {table.mode} · key {table.key.join(', ') || 'none'} · {table.inserted} inserted /{' '}
                  {table.updated} updated / {table.skipped} skipped
                </span>
                {detail.target && table.status === 'written' ? (
                  <a
                    href={workbenchHref(detail.target)}
                    className="inline-flex items-center gap-1 text-st-info hover:underline"
                  >
                    Open in SQL workbench <ExternalLink className="size-3" />
                  </a>
                ) : null}
              </div>
              {table.status === 'failed' && table.issues?.length ? (
                <ul className="mt-2 list-disc pl-4 text-11 text-st-down">
                  {table.issues.slice(0, 3).map((issue, index) => (
                    <li key={index}>{issue.message}</li>
                  ))}
                </ul>
              ) : null}
            </TabsContent>
          )
        })}
      </Tabs>
    </Card>
  )
}

function PagesSummary({ pages }: { pages: NonNullable<TabularIngestJobDetail['pages']> }) {
  const mismatch = pages.filter(
    (page) => page.status === 'mismatch' || page.status === 'unreadable'
  ).length
  const skipped = pages.filter((page) => page.status === 'skipped').length
  return (
    <p className="mb-2 text-11 text-ink-3">
      {pages.length} pages · {pages.filter((page) => page.status === 'ok').length} ok ·{' '}
      {pages.filter((page) => page.status === 'needs_review').length} needs review ·{' '}
      {pages.filter((page) => page.status === 'blank').length} blank
      {skipped ? ` · ${skipped} skipped` : ''}
      {mismatch ? ` · ${mismatch} did not match` : ''}
    </p>
  )
}

function ChecksCard({ detail }: { detail: TabularIngestJobDetail }) {
  const passed = detail.checks.filter((check) => check.passed).length
  return (
    <Card className="min-w-0 gap-2 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className={SECTION_LABEL}>Checks</h2>
        {detail.checks.length ? (
          <span className="text-11 text-ink-3">
            {passed}/{detail.checks.length} passed
          </span>
        ) : null}
      </div>
      {detail.checks.length ? (
        <ul className="flex flex-col gap-1 text-xs leading-5">
          {detail.checks.map((check) => (
            <li
              key={check.index}
              className={cn('flex min-w-0 gap-2', check.passed ? 'text-st-run' : 'text-st-down')}
            >
              <span className="shrink-0 select-none">{check.passed ? '✓' : '✕'}</span>
              <span className="min-w-0 break-words">
                <span className="font-mono">{check.table}</span> · {check.message}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-ink-3">not run</p>
      )}
    </Card>
  )
}

function InputCard({ detail }: { detail: TabularIngestJobDetail }) {
  const [downloadError, setDownloadError] = React.useState<string>()
  const { openSource, viewerProps } = useSourceDocViewer()
  // An intake keeps its own copy of the file whether or not the run stored one.
  const intakeItemId = detail.source.kind === 'intake' ? detail.source.itemId : undefined
  const downloadable = !!intakeItemId || detail.inputsStaged
  const inputName = (name: string, className: string) =>
    downloadable ? (
      <button
        type="button"
        className={cn(className, 'cursor-pointer text-left hover:underline')}
        title={`Preview ${name}`}
        onClick={() =>
          openSource({
            sourceFile: name,
            supportsOriginalDownload: false,
            loadSource: () =>
              intakeItemId
                ? fetchIntakeItemFile(intakeItemId)
                : fetchTabularRunInput(detail.id, name)
          })
        }
      >
        {name}
      </button>
    ) : (
      <p className={className}>{name}</p>
    )
  const download = (fileName: string) => {
    setDownloadError(undefined)
    const saving = intakeItemId
      ? downloadIntakeItemFile(intakeItemId, fileName)
      : downloadTabularRunInput(detail.id, fileName)
    saving.catch((error: unknown) =>
      setDownloadError(error instanceof Error ? error.message : 'The file could not be downloaded.')
    )
  }
  const downloadButton = (fileName: string) =>
    downloadable ? (
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={cn(COMPACT_ACTION, 'shrink-0')}
        aria-label={`Download ${fileName}`}
        onClick={() => download(fileName)}
      >
        <Download className="size-3" aria-hidden />
        Download
      </Button>
    ) : null
  return (
    <Card className="min-w-0 gap-2 p-3">
      <h2 className={SECTION_LABEL}>Input</h2>
      {detail.files.length === 0
        ? detail.expectedFiles.map((file) => (
            <div
              key={file.name}
              className="flex min-w-0 items-start justify-between gap-3 rounded-md border border-line px-3 py-2 text-xs leading-5"
            >
              <div className="min-w-0">
                {inputName(file.name, 'block font-medium break-words text-ink')}
                {file.bytes !== undefined ? (
                  <p className="text-11 text-ink-3">{formatBytes(file.bytes)}</p>
                ) : null}
                {detail.inputsStaged ? null : (
                  <p className="text-ink-2">
                    {intakeItemId
                      ? 'The run did not store this file; the download is the copy the intake received.'
                      : 'The run failed before it stored this file, so there is no copy to download.'}
                  </p>
                )}
              </div>
              {downloadButton(file.name)}
            </div>
          ))
        : null}
      {detail.files.map((file) => (
        <div
          key={`${file.name}-${file.sha256}`}
          className="min-w-0 rounded-md border border-line px-3 py-2 text-xs leading-5"
        >
          <div className="flex min-w-0 items-start justify-between gap-3">
            {inputName(file.name, 'min-w-0 font-medium break-words text-ink')}
            {downloadButton(file.name)}
          </div>
          <p className={cn(MONO_WRAP, 'text-11 text-ink-3')}>sha256 {file.sha256}</p>
          {detail.parserKind === 'script' ? (
            <p className="text-ink-2">
              Fingerprint:{' '}
              {file.fingerprint?.matched === false
                ? 'did not match'
                : `matched v${detail.configVersion}`}
            </p>
          ) : (
            <p className="text-ink-2">
              Config selected by {sourceLabel(detail.source)}; the run never re-identifies the file.
            </p>
          )}
          {file.summary ? <p className="text-ink-3">{file.summary}</p> : null}
        </div>
      ))}
      {downloadError ? (
        <p role="alert" className="text-xs text-st-down">
          {downloadError}
        </p>
      ) : null}
      <SourceDocViewer {...viewerProps} />
    </Card>
  )
}

/** The extraction agent's tool calls, drawn like the config builder's. */
function ToolCallsCard({ detail }: { detail: TabularIngestJobDetail }) {
  if (detail.parserKind !== 'agent') return null
  const live = ['uploading', 'processing', 'cancelling'].includes(detail.state)
  if (!detail.conversationId) {
    return (
      <CollapsibleCard title="Tool calls" defaultOpen testId="run-tool-calls-unavailable">
        <p className="text-xs text-ink-3">
          {live
            ? "The agent's tool calls appear here once it starts."
            : 'No tool calls were recorded for this run.'}
        </p>
      </CollapsibleCard>
    )
  }
  return (
    <ConversationToolCallsCard
      conversationId={detail.conversationId}
      live={live}
      settledKey={detail.completedAt ?? detail.state}
      emptyText="The agent has not called any tools yet."
      loadErrorText="Failed to load the agent's tool calls."
      listClassName="max-h-[32rem] overflow-y-auto"
      collapsible
      testId="run-tool-calls"
    />
  )
}

function LogsCard({ detail }: { detail: TabularIngestJobDetail }) {
  return (
    <CollapsibleCard
      title="Logs"
      meta={detail.logCount ? plural(detail.logCount, 'entry', 'entries') : undefined}
      testId="run-logs"
    >
      <div className="max-h-72 min-w-0 overflow-y-auto">
        <JobLogs jobId={detail.id} />
      </div>
    </CollapsibleCard>
  )
}
